import { Check, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@web/components/ui/button.tsx";
import { usePublishLocalPlaylist } from "@web/features/library/api.ts";
import { formatDateTime } from "@web/lib/format.ts";

/** One-way create: workspace playlist → a new Spotify playlist. */
export function PlaylistPublishSection({
  localPlaylistId,
  spotifyUrl,
  publishedAt,
}: {
  localPlaylistId: string;
  spotifyUrl: string | null;
  publishedAt: string | null;
}) {
  const publish = usePublishLocalPlaylist();
  const created = spotifyUrl !== null;
  const busy = publish.isPending;

  return (
    <section className="space-y-2 rounded-md border border-line bg-panel px-3 py-3">
      <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
        Spotify
      </h3>
      {created && (
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm text-accent">
            <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
            On Spotify
          </span>
          {spotifyUrl && (
            <a
              href={spotifyUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
            >
              Open
              <ExternalLink className="size-3" aria-hidden />
            </a>
          )}
        </div>
      )}
      {created && publishedAt && (
        <p className="text-xs text-muted">Last created {formatDateTime(publishedAt)}</p>
      )}
      <Button
        type="button"
        variant={created ? "outline" : "default"}
        disabled={busy}
        onClick={() => publish.mutate(localPlaylistId)}
      >
        {busy ? (
          <>
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Creating…
          </>
        ) : created ? (
          "Create another on Spotify"
        ) : (
          "Create on Spotify"
        )}
      </Button>
      <p className="text-xs text-muted">
        Copies this playlist to your Spotify account. Each click makes a new one.
      </p>
    </section>
  );
}
